import '@ckeditor/ckeditor5-build-classic/build/translations/fr';
import 'moment/locale/fr';

import { DOCUMENT, registerLocaleData } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localePt from '@angular/common/locales/pt';
import { APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AkitaNgRouterStoreModule } from '@datorama/akita-ng-router-store';
import { AkitaNgDevtools } from '@datorama/akita-ngdevtools';
import {
	TranslateLoader,
	TranslateModule,
	TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { BdcWalkModule } from 'bdc-walkthrough';
import * as LogRocket from 'logrocket';
import { locale as momentLocale } from 'moment';
import { NgScrollbarReachedModule } from 'ngx-scrollbar/reached-event';

import { MoodDescriptionDialogModule } from 'dialog/mood-description-dialog/mood-description-dialog.module';
import { environment } from 'environments/environment';
import { AppChatModule } from 'presentation/app-chat/app-chat.module';
import { AppSidebarModule } from 'presentation/app-sidebar/app-sidebar.module';
import { AppToolbarComponent } from 'presentation/app-toolbar/app-toolbar.component';
import { AppComponent } from 'presentation/app.component';
import { PageWithTitleModule } from 'presentation/common/component/page-with-title/page-with-title.module';
import { CoachProfileBiographyComponent } from 'presentation/page/profile/coach-profile/coach-profile-biography/coach-profile-biography.component';
import { CoachProfileCertificatesComponent } from 'presentation/page/profile/coach-profile/coach-profile-certificates/coach-profile-certificates.component';
import { CoachProfileDiplomasComponent } from 'presentation/page/profile/coach-profile/coach-profile-diplomas/coach-profile-diplomas.component';
import { CoachProfileInformationComponent } from 'presentation/page/profile/coach-profile/coach-profile-information/coach-profile-information.component';
import { CoachProfileSectorsComponent } from 'presentation/page/profile/coach-profile/coach-profile-sectors/coach-profile-sectors.component';
import { CoachProfileComponent } from 'presentation/page/profile/coach-profile/coach-profile.component';
import { CoacheeProfileModule } from 'presentation/page/profile/coachee-profile/coachee-profile.module';
import { SettingsModule } from 'presentation/page/settings/settings.module';

import version from '../../package.json';
import { AppInfrastructureModule } from './app-infrastructure.module';
import { AppRepositoryModule } from './app-repository.module';
import { AppRoutingModule } from './app-routing.module';
import { AppServiceModule } from './app-service.module';
import { AppSharedModule } from './app-shared.module';
import { AppLocator } from './app.locator';

registerLocaleData(localeFr, 'fr');
registerLocaleData(localeEn, 'en');
registerLocaleData(localePt, 'pt');

export function initApp(
	translate: TranslateService,
	document: Document
): () => Promise<unknown> {
	return () => {
		if (environment.production && document.location.host !== environment.domain) {
			document.location.href = `${document.location.protocol}//${environment.domain}${document.location.pathname}`;
			return Promise.reject();
		}

		if (environment.logRocketEnabled) {
			LogRocket.init('mab-ventures/spreeng-public', { release: version.version });
		}

		const language = environment.availableLocales
			.map((loc) => loc.lang)
			.includes(translate.getBrowserLang())
			? translate.getBrowserLang()
			: 'en';

		translate.setDefaultLang(language);
		momentLocale(language);

		return translate.use(language).toPromise();
	};
}

export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
	return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

@NgModule({
	declarations: [
		AppComponent,
		AppToolbarComponent,
		CoachProfileComponent,
		CoachProfileInformationComponent,
		CoachProfileSectorsComponent,
		CoachProfileBiographyComponent,
		CoachProfileDiplomasComponent,
		CoachProfileCertificatesComponent,
	],
	imports: [
		BdcWalkModule,
		AppChatModule,
		BrowserModule,
		SettingsModule,
		AppSharedModule,
		AppRoutingModule,
		HttpClientModule,
		AppSidebarModule,
		AppServiceModule,
		AppRepositoryModule,
		NgScrollbarReachedModule,
		AppInfrastructureModule,
		BrowserAnimationsModule,
		AkitaNgRouterStoreModule,
		MoodDescriptionDialogModule,
		CoacheeProfileModule,
		environment.production ? [] : [AkitaNgDevtools.forRoot()],
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: createTranslateLoader,
				deps: [HttpClient],
			},
		}),
		ServiceWorkerModule.register('ngsw-worker.js', {
			registrationStrategy: 'registerImmediately',
			enabled: environment.production,
		}),
		PageWithTitleModule,
	],
	providers: [
		{
			deps: [TranslateService, DOCUMENT],
			provide: APP_INITIALIZER,
			useFactory: initApp,
			multi: true,
		},
	],
	bootstrap: [AppComponent],
})
export class AppModule {
	constructor(injector: Injector) {
		AppLocator.injector = injector;
	}
}
